/**
 * Farmland's COA review module's own per-operation functions (FLF-S2), on `acceptanceReferences.ts`'s exact
 * shape: named functions plus a query-key factory, so a component never writes a path, a method or a query
 * key.
 *
 * **Uploading through this module's own `/v1/coa/reviews/upload`, not the kernel's `POST /v1/files`
 * directly.** The locked design called for the frontend to call `uploadPortalFile` from
 * `@equerranz/portal-api-client` directly, the same generic kernel operation the framework's own branding
 * settings page uses. That package's `usePortalModuleTransport` deliberately gives a module frontend a
 * transport bound to its own `/v1/coa/*` prefix and nothing else (`createPortalModuleTransport`'s own path
 * filter), and exposes no way to reach the portal's raw `PortalApiClient` a kernel-level operation needs; a
 * module frontend may not depend on `@equerranz/portal-shell`, the one package that holds it, on the
 * identical boundary the .NET side enforces. So this module's own backend exposes a thin upload endpoint
 * under its own prefix instead, reusing `IFileStore` exactly as the design intended (see
 * `CoaReviewEndpoints.UploadReviewFileAsync`'s own remarks). `uploadCoaReviewFile` below builds the same
 * `FormData` shape `uploadPortalFile` does, sent through this module's own transport rather than the raw
 * client.
 */
import { PortalApiError } from '@equerranz/portal-api-client';
import { COA_SLUG } from './acceptanceReferences.js';
import { coaListParams } from './coaLists.js';
/**
 * Query keys as a factory rather than as literals at each call site, prefixed with the module's slug on
 * `acceptanceReferences.ts`'s own convention.
 */
export const coaReviewQueryKeys = {
    knownItemCodes: () => ['module', COA_SLUG, 'known-item-codes'],
    /** The key the Items page's interactive list caches each view under: a sub-key of `knownItemCodes`, so a write that invalidates that refreshes every view. */
    knownItemCodeList: () => ['module', COA_SLUG, 'known-item-codes', 'list'],
    reviewHistory: () => ['module', COA_SLUG, 'reviews', 'history'],
    /** The key the History page's interactive list caches each view under: a sub-key of `reviewHistory`, so a write that invalidates that refreshes every view. */
    reviewHistoryList: () => ['module', COA_SLUG, 'reviews', 'history', 'list'],
    reviewDetail: (id) => ['module', COA_SLUG, 'reviews', 'history', id],
};
/**
 * Reads this tenant's known item codes. With no `query` it asks for the default view, exactly as it always has: the
 * newest page, with no query string. With one it asks for a page on the shared list contract.
 */
export function listKnownItemCodes(transport, options) {
    return transport.send({
        method: 'GET',
        path: '/v1/coa/known-item-codes',
        ...coaListParams(options?.query),
        ...(options?.signal === undefined ? {} : { signal: options.signal }),
    });
}
/** Adds an item code to this tenant's known item codes. */
export function createKnownItemCode(transport, request) {
    return transport.send({
        method: 'POST',
        path: '/v1/coa/known-item-codes',
        body: request,
    });
}
/** Deletes one known item code for this tenant. */
export function deleteKnownItemCode(transport, id) {
    return transport.send({
        method: 'DELETE',
        path: '/v1/coa/known-item-codes/{id}',
        params: { path: { id } },
    });
}
/** The known-item-codes read, shaped for TanStack Query. */
export function knownItemCodesQueryOptions(transport) {
    return {
        queryKey: coaReviewQueryKeys.knownItemCodes(),
        queryFn: (context) => listKnownItemCodes(transport, { signal: context.signal }),
    };
}
/**
 * Uploads `file` into this tenant's own file store under a fresh, module-minted key, and returns the entry
 * the store now holds. See this file's own header comment for why this goes through this module's own
 * transport rather than the kernel's `uploadPortalFile`.
 */
export function uploadCoaReviewFile(transport, file) {
    const body = new FormData();
    body.set('file', file, file instanceof File ? file.name : 'upload.pdf');
    return transport.send({
        method: 'POST',
        path: '/v1/coa/reviews/upload',
        body,
    });
}
/** Reviews a previously uploaded COA submission: extracts, evaluates and matches, writing nothing. */
export function reviewCoaSubmission(transport, request) {
    return transport.send({
        method: 'POST',
        path: '/v1/coa/reviews',
        body: request,
    });
}
/**
 * The two refusals a review can answer with that the person can fix by choosing another file (framework convention R145):
 * `coa.review.unparseable` (an unreadable or password-protected PDF, or a document that did not read as a COA) and
 * `coa.review.invalid` (the file was not usable as sent). Both are 400s about the file itself. Read from the error's code,
 * never from its message. A 502 `coa.review.extraction-failed`, a 409 `coa.review.extraction-not-configured` and a network
 * failure are faults the person cannot fix, so they are not here.
 */
export const COA_FIXABLE_FILE_REFUSAL_CODES = ['coa.review.unparseable', 'coa.review.invalid'];
/** Whether a failed review is one the person fixes by choosing a different file. */
export function isFixableFileRefusal(failure) {
    return failure instanceof PortalApiError && COA_FIXABLE_FILE_REFUSAL_CODES.includes(failure.code);
}
/**
 * Extracts, evaluates and aggregates a COA PDF's review exactly as `reviewCoaSubmission` does, and persists
 * one header, one composition line per constituent lot and one row per test row, in a single call (FLF-S3).
 * This module's own review page calls this, not `reviewCoaSubmission`, as its save path (FLF-S4): a review is
 * only worth a QA reviewer's time once it can also be confirmed.
 */
export function persistCoaReview(transport, request) {
    return transport.send({
        method: 'POST',
        path: '/v1/coa/reviews/persist',
        body: request,
    });
}
/**
 * Confirms a persisted review and sends its outcome to Farmland's systems, whatever that outcome is (FLF-S4,
 * R66): a Fail or Unrecognised composite raises its own `coa.outcome-determined` event per lot exactly as a
 * Pass composite does. Idempotent by the review's own state, not by an `Idempotency-Key`: confirming an
 * already-confirmed id answers with its existing confirmed state rather than erroring or re-sending.
 */
export function confirmCoaReview(transport, id) {
    return transport.send({
        method: 'POST',
        path: '/v1/coa/reviews/{id}/confirm',
        params: { path: { id } },
    });
}
/**
 * Lists this tenant's confirmed COA reviews, one row per lot (FLF-S9): a composite is out of this list until it has been
 * confirmed, whatever its own upload or persist state. With no `query` it asks for the default view, exactly as it always
 * has: the newest confirmed first, with no query string. With one it asks for a page on the shared list contract.
 */
export function listCoaReviews(transport, options) {
    return transport.send({
        method: 'GET',
        path: '/v1/coa/reviews',
        ...coaListParams(options?.query),
        ...(options?.signal === undefined ? {} : { signal: options.signal }),
    });
}
/**
 * Reads one confirmed COA review's full composition entries and test rows (FLF-S9), the same detail the
 * original review screen showed while it was in progress. A persisted-but-not-yet-confirmed review 404s here
 * exactly as an unknown id does (AC3).
 */
export function getCoaReview(transport, id, options) {
    return transport.send({
        method: 'GET',
        path: '/v1/coa/reviews/{id}',
        params: { path: { id } },
        ...(options?.signal === undefined ? {} : { signal: options.signal }),
    });
}
/** One confirmed review's own detail read, shaped for TanStack Query. */
export function reviewDetailQueryOptions(transport, id) {
    return {
        queryKey: coaReviewQueryKeys.reviewDetail(id),
        queryFn: (context) => getCoaReview(transport, id, { signal: context.signal }),
    };
}
//# sourceMappingURL=coaReviews.js.map